import { useQuery } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { getCampaignCuration } from '@/api/beatlyApi';

export interface UseCurationParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/** API expects campaign IDs like "123456-cpg"; avoid calling with invalid ids to prevent 404/500. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * Fetches curation (suggested creators) for a campaign.
 * Same API as beatly-web-app: GET /app/v2/client/campaign/:campaignId/curation
 */
export function useCuration({ campaignId, enabled = true }: UseCurationParams) {
  const token = getTokenFromStorage();
  const shouldFetch = enabled && Boolean(token && campaignId) && isValidCampaignId(campaignId);

  const { data, isLoading, isFetching, isFetched, error, isError, refetch } = useQuery({
    queryKey: ['client', 'campaign', campaignId, 'curation'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID is required');
      return getCampaignCuration(campaignId, token ?? '');
    },
    enabled: shouldFetch,
  });

  return {
    data: data ?? [],
    isLoading,
    isFetching,
    isFetched,
    error,
    isError,
    refetch,
  };
}
